import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { Loader2, Lock, Pencil } from "lucide-react";

type Student = { id: string; first_name: string; last_name: string; class: string | null; current_semester?: string | null };

export type AccountingProfile = {
  payment_category: string | null;
  has_nhif: boolean;
  resides_hostel: boolean;
  has_uniform: boolean;
  has_medical_equipment: boolean;
};

const DEFAULTS: AccountingProfile = {
  payment_category: null,
  has_nhif: false,
  resides_hostel: false,
  has_uniform: true,
  has_medical_equipment: true,
};

const PAYMENT_CATEGORIES = [
  { value: "full_payment", label: "Full payment" },
  { value: "got_scholarship", label: "GoT scholarship" },
  { value: "private_scholarship", label: "Private scholarship" },
  { value: "full_scholarship", label: "Full scholarship" },
  { value: "exempted", label: "Exempted" },
];

const COST_CODES = ["NHF", "HOSTEL", "UNIFORM", "MEDICAL_EQUIPMENT"] as const;

// Returns "bill" decision for each code given profile answers
function shouldBill(code: string, p: AccountingProfile): boolean {
  if (code === "NHF") return !p.has_nhif;
  if (code === "HOSTEL") return p.resides_hostel;
  if (code === "UNIFORM") return !p.has_uniform;
  if (code === "MEDICAL_EQUIPMENT") return !p.has_medical_equipment;
  return false;
}

export type AccountingPlanItem = { code: string; name: string; amount: number };
export type AccountingPlan = { created: number; removed: number; toCreate: AccountingPlanItem[]; toRemove: AccountingPlanItem[] };

export async function applyAccountingProfile(
  student: { id: string; class: string | null; current_semester?: string | null },
  profile: AccountingProfile,
  updatedBy: string | null,
  opts: { dryRun?: boolean } = {},
): Promise<AccountingPlan> {
  const dryRun = !!opts.dryRun;

  // 1. Upsert profile row (skip in dry-run)
  if (!dryRun) {
    const { error: upErr } = await supabase
      .from("student_accounting_profile")
      .upsert({
        student_id: student.id,
        payment_category: profile.payment_category,
        has_nhif: profile.has_nhif,
        resides_hostel: profile.resides_hostel,
        has_uniform: profile.has_uniform,
        has_medical_equipment: profile.has_medical_equipment,
        updated_by: updatedBy,
      });
    if (upErr) throw upErr;
  }

  if (!student.class) return { created: 0, removed: 0, toCreate: [], toRemove: [] };

  // 2. Find matching fee_category for this student's class
  const { data: cats } = await supabase
    .from("fee_categories")
    .select("id, classes, academic_year")
    .contains("classes", [student.class]);
  if (!cats || cats.length === 0) return { created: 0, removed: 0, toCreate: [], toRemove: [] };
  // Pick latest academic_year
  const cat = cats.sort((a, b) => (b.academic_year || "").localeCompare(a.academic_year || ""))[0];

  const semester = (student.current_semester || "I").toUpperCase().includes("II") ? "II" : "I";

  // 3. Load items for this category + semester + relevant cost types
  const { data: items } = await supabase
    .from("fee_structure_items")
    .select("id, amount, cost_type_id, semester, fee_cost_types(code, name)")
    .eq("category_id", cat.id)
    .eq("semester", semester);
  const targetItems = (items || []).filter((it: any) => COST_CODES.includes(it.fee_cost_types?.code));

  // 4. Load existing 'other' invoices for student
  const { data: existing } = await supabase
    .from("invoices")
    .select("id, cost_type_id, status")
    .eq("student_id", student.id)
    .eq("kind", "other");

  let created = 0;
  let removed = 0;
  const toInsert: any[] = [];
  const toDelete: string[] = [];
  const toCreate: AccountingPlanItem[] = [];
  const toRemove: AccountingPlanItem[] = [];

  for (const it of targetItems) {
    const code = (it.fee_cost_types as any)?.code as string;
    const name = (it.fee_cost_types as any)?.name ?? "Other";
    const amount = Number(it.amount) || 0;
    const bill = shouldBill(code, profile);
    const existingInv = (existing || []).find((e: any) => e.cost_type_id === it.cost_type_id);
    if (bill && amount > 0) {
      if (!existingInv) {
        toCreate.push({ code, name, amount });
        toInsert.push({
          student_id: student.id,
          amount,
          description: `${name} (Sem ${it.semester})`,
          status: "unpaid",
          kind: "other",
          cost_type_id: it.cost_type_id,
        });
      }
    } else {
      if (existingInv && existingInv.status === "unpaid") {
        toRemove.push({ code, name, amount });
        toDelete.push(existingInv.id);
      }
    }
  }

  if (dryRun) {
    return { created: toInsert.length, removed: toDelete.length, toCreate, toRemove };
  }

  if (toInsert.length) {
    const { error } = await supabase.from("invoices").insert(toInsert);
    if (error) throw error;
    created = toInsert.length;
  }
  if (toDelete.length) {
    const { error } = await supabase.from("invoices").delete().in("id", toDelete);
    if (error) throw error;
    removed = toDelete.length;
  }
  return { created, removed, toCreate, toRemove };
}

export function AccountingDetailsDialog({
  student,
  open,
  onOpenChange,
}: {
  student: Student | null;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const [profile, setProfile] = useState<AccountingProfile>(DEFAULTS);
  const [savedProfile, setSavedProfile] = useState<AccountingProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!student || !open) return;
    setLoading(true);
    setEditing(false);
    (async () => {
      const { data } = await supabase
        .from("student_accounting_profile")
        .select("*")
        .eq("student_id", student.id)
        .maybeSingle();
      if (data) {
        const p: AccountingProfile = {
          payment_category: data.payment_category,
          has_nhif: data.has_nhif,
          resides_hostel: data.resides_hostel,
          has_uniform: data.has_uniform,
          has_medical_equipment: data.has_medical_equipment,
        };
        setProfile(p);
        setSavedProfile(p);
        setEditing(false);
      } else {
        setProfile(DEFAULTS);
        setSavedProfile(null);
        setEditing(true);
      }
      setLoading(false);
    })();
  }, [student, open]);

  const locked = !editing && savedProfile !== null;

  const handleSave = async () => {
    if (!student) return;
    setSaving(true);
    try {
      const { data: u } = await supabase.auth.getUser();
      const { created, removed } = await applyAccountingProfile(student, profile, u?.user?.id ?? null);
      toast({ title: "Saved", description: `${created} invoice${created !== 1 ? "s" : ""} created, ${removed} removed.` });
      onOpenChange(false);
    } catch (e: any) {
      toast({ title: "Failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (savedProfile) {
      setProfile(savedProfile);
      setEditing(false);
    } else {
      onOpenChange(false);
    }
  };

  const YesNo = ({ label, value, onChange }: { label: string; value: boolean; onChange: (v: boolean) => void }) => (
    <div className="flex items-center justify-between gap-4 py-2">
      <Label className="text-sm">{label}</Label>
      {locked ? (
        <Badge variant={value ? "default" : "secondary"}>{value ? "Yes" : "No"}</Badge>
      ) : (
        <RadioGroup value={value ? "yes" : "no"} onValueChange={(v) => onChange(v === "yes")} className="flex gap-4">
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <RadioGroupItem value="yes" /> Yes
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <RadioGroupItem value="no" /> No
          </label>
        </RadioGroup>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Add Accounting Details
            {locked && <Lock className="h-4 w-4 text-muted-foreground" aria-label="Locked" />}
          </DialogTitle>
          <DialogDescription>
            {student ? `${student.first_name} ${student.last_name} — ${student.class ?? "—"}` : ""}
            {locked && <span className="ml-1 text-xs">(locked)</span>}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Payment Category</Label>
              {locked ? (
                <div className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                  {PAYMENT_CATEGORIES.find((c) => c.value === profile.payment_category)?.label ?? "—"}
                </div>
              ) : (
                <Select
                  value={profile.payment_category ?? ""}
                  onValueChange={(v) => setProfile((p) => ({ ...p, payment_category: v }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {PAYMENT_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="border-t pt-2">
              <YesNo label="Have NHIF Card?" value={profile.has_nhif} onChange={(v) => setProfile((p) => ({ ...p, has_nhif: v }))} />
              <YesNo label="Resides in Hostel?" value={profile.resides_hostel} onChange={(v) => setProfile((p) => ({ ...p, resides_hostel: v }))} />
              <YesNo label="Have Uniforms?" value={profile.has_uniform} onChange={(v) => setProfile((p) => ({ ...p, has_uniform: v }))} />
              <YesNo label="Have Medical Equipment?" value={profile.has_medical_equipment} onChange={(v) => setProfile((p) => ({ ...p, has_medical_equipment: v }))} />
            </div>

            {!locked && (
              <p className="text-xs text-muted-foreground">
                Saving will auto-create invoices for items the student lacks (NHIF, Uniform, Medical Equipment) and for Hostel if they reside in one. Existing unpaid invoices for items now marked as owned will be removed.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          {locked ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving || loading}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
